import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check, Sparkles } from "lucide-react";
import { useT } from "@/i18n";
import { fmtPrice } from "@/lib/content";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const recommendPackage = (a) => (a[0] === "b" ? "mini" : a[1] === "b" ? "allinclusive" : "smart");
export const quizQuestions = (q) => [{ text: q.q1, a: q.q1a, b: q.q1b }, { text: q.q2, a: q.q2a, b: q.q2b }];
export const quizDone = (answers, n) => answers.length === n || answers[0] === "b";

export default function PackageQuiz({ open, onOpenChange, packages, onOrder }) {
  const { t, lang } = useT();
  const q = t.quiz;
  const [answers, setAnswers] = useState([]);
  const step = answers.length;
  const questions = quizQuestions(q);

  const reset = () => setAnswers([]);
  const close = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const finished = quizDone(answers, questions.length);
  const resultId = finished ? recommendPackage(answers) : null;
  const pkg = resultId ? packages.find((p) => p.id === resultId) : null;
  const trPkg = pkg ? (t.pricing.pkgs && t.pricing.pkgs[pkg.id]) || {} : {};
  const pkgName = pkg ? (lang === "pl" ? pkg.name : trPkg.name || pkg.name) : "";
  const features = pkg ? (lang === "pl" ? pkg.features : trPkg.features || pkg.features) : [];
  const price = pkg ? pkg.price_annual || pkg.price_6 || pkg.price_monthly : "";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="quiz-dialog" className="max-w-lg rounded-2xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-6 sm:p-8 pb-0 text-left">
          <DialogTitle className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
            <Sparkles size={20} className="text-slate-500" /> {q.title}
          </DialogTitle>
          <DialogDescription className="text-sm">{q.sub}</DialogDescription>
        </DialogHeader>

        <div className="p-6 sm:p-8">
          <AnimatePresence initial={false}>
            {!pkg ? (
              <motion.div key={`q-${step}`} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
                <div className="flex items-center justify-between">
                  <span data-testid="quiz-step" className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{q.step.replace("{n}", step + 1)}</span>
                  <div className="flex gap-1.5">
                    {questions.map((_, i) => <span key={i} className={`h-1.5 w-8 rounded-full ${i <= step ? "bg-brand-teal" : "bg-muted"}`} />)}
                  </div>
                </div>
                <h3 data-testid="quiz-question" className="font-display font-bold text-lg sm:text-xl tracking-tight mt-4">{questions[step].text}</h3>
                <div className="mt-5 grid gap-3">
                  {["a", "b"].map((opt) => (
                    <button
                      key={opt}
                      data-testid={`quiz-answer-${opt}`}
                      onClick={() => setAnswers([...answers, opt])}
                      className="text-left rounded-xl border border-border bg-background px-5 py-4 text-sm font-medium hover:border-slate-500 hover:bg-muted transition-colors"
                    >
                      {questions[step][opt]}
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button data-testid="quiz-back" onClick={() => setAnswers(answers.slice(0, -1))} className="mt-5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                    <ArrowLeft size={13} /> {q.back}
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div key="result" data-testid="quiz-result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{q.result_overline}</span>
                <div className="mt-3 rounded-2xl bg-brand-navy text-white p-6">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <h3 data-testid="quiz-result-name" className="font-display font-bold text-2xl tracking-tight">{pkgName}</h3>
                    <span className="font-mono text-sm opacity-80">{fmtPrice(price)} {t.pricing.per_month}</span>
                  </div>
                  {pkg.price_annual && <p data-testid="quiz-result-upfront-note" className="text-xs mt-1 font-semibold text-brand-yellow/90">{t.pricing.upfront_note.replace("{m}", "12")}</p>}
                  <p className="text-sm opacity-80 mt-2 leading-relaxed">{q.result_reason[pkg.id]}</p>
                  <ul className="mt-4 space-y-1.5">
                    {features.slice(0, 4).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm"><Check size={14} className="mt-0.5 shrink-0 opacity-70" />{f.replace(/^!\s*/, "")}</li>
                    ))}
                  </ul>
                </div>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="quiz-order-button"
                    onClick={() => { close(false); onOrder(pkg.id); }}
                    className="flex-1 bg-brand-teal text-white hover:bg-brand-tealDark font-semibold py-3 rounded-full text-sm transition-colors"
                  >
                    {q.order} {pkgName}
                  </button>
                  <button data-testid="quiz-restart" onClick={reset} className="px-5 py-3 rounded-full text-sm font-semibold border border-border hover:bg-muted transition-colors">
                    {q.restart}
                  </button>
                </div>
                <button data-testid="quiz-see-all" onClick={() => close(false)} className="mt-4 text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground">
                  {q.other}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
